import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import QRCode from 'qrcode';
import { createRental } from '../api/services/rentalsService';

interface BookingConfirmationPageProps {
  bookingDetails?: {
    vehicle: any;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    totalPrice: number;
    totalHours: number;
  };
  paymentDetails?: any;
}

const BookingConfirmationPage: React.FC<BookingConfirmationPageProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [showAnimation, setShowAnimation] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [rentalCreated, setRentalCreated] = useState(false);
  const [trip_token, setTripToken] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const bookingDetails = location.state?.bookingDetails;
  const paymentDetails = location.state?.paymentDetails;
  const rentalId = location.state?.rentalId;
  const rentalResponse = location.state?.rentalResponse; // Nouvelle prop pour la réponse complète

  useEffect(() => {
    if (!bookingDetails) {
      navigate('/overview');
      return;
    }
    setTimeout(() => setShowAnimation(true), 100);
  }, [bookingDetails, navigate]);

  // Générer le QR code avec le trip_token reçu de l'API
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        // Utiliser le trip_token reçu de la réponse API
        let accessCode = '1234'; // Code par défaut
        if (rentalResponse?.trip_token) {
          setTripToken(rentalResponse.trip_token);
          accessCode = rentalResponse.trip_token;
        } else if (rentalId && rentalId !== 'N/A') {
          // Fallback si pas de trip_token dans la réponse
          const fallbackToken = rentalId.slice(-4); // Utiliser les 4 derniers caractères de l'ID
          setTripToken(fallbackToken);
          accessCode = fallbackToken;
        } else {
          setTripToken('1234');
        }

        const qrDataUrl = await QRCode.toDataURL(accessCode, {
          width: 120,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataUrl(qrDataUrl);
      } catch (error) {
        console.error('Erreur lors de la génération du QR code:', error);
      }
    };

    generateQRCode();
  }, [rentalResponse, rentalId]);

  const getVehicleEmoji = (type: string) => {
    switch (type) {
      case 'car': return '🚗';
      case 'motorcycle': return '🏍️';
      case 'scooter': return '🛴';
      case 'bike': return '🚲';
      default: return '🚗';
    }
  };

  const handleViewRentals = () => navigate('/mytrips');
  const handleBackToOverview = () => navigate('/overview');
  const handleShareBooking = async () => {
    const shareText = `🚗 Réservation OpenDingo\n\nVéhicule: ${bookingDetails?.vehicle.title}\nPrix: ${bookingDetails?.totalPrice}€\nCode d'accès: ${trip_token}\n\nRendez-vous au véhicule et scannez le QR code !`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Ma réservation OpenDingo',
          text: shareText,
          url: window.location.href
        });
      } else {
        // Si l'API Share n'est pas disponible, ne rien faire
        console.log('API Share non disponible sur ce navigateur');
      }
    } catch (error) {
      console.error('Erreur lors du partage:', error);
      // Ne pas afficher d'alerte en cas d'erreur
    }
  };

  if (!bookingDetails) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-indigo-50 to-purple-50 overflow-y-auto">
      <div className="w-full bg-white p-4 sm:p-6 lg:p-8">
        
        {/* Header - Succès */}
        <div className={`text-center mb-4 sm:mb-6 transition-all duration-700 ${showAnimation ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">Paiement réussi !</h1>
          <p className="text-sm text-gray-600">Réservation confirmée</p>
        </div>

        {/* Contenu principal */}
        <div className="max-w-3xl mx-auto space-y-3 sm:space-y-4 pb-6">
          
          {/* Détails du véhicule */}
          <div className={`bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 sm:p-4 transition-all duration-700 delay-200 ${showAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center mb-2 sm:mb-3">
              <span className="text-xl sm:text-2xl mr-2">{getVehicleEmoji(bookingDetails.vehicle.type)}</span>
              <h2 className="font-semibold text-sm sm:text-base text-gray-900">Véhicule</h2>
            </div>
            <div className="text-xs sm:text-sm space-y-1 sm:space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Nom:</span>
                <span className="font-medium">{bookingDetails.vehicle.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium capitalize">{bookingDetails.vehicle.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Prix:</span>
                <span className="font-semibold text-indigo-600">{bookingDetails.vehicle.hourly_rate}€/h</span>
              </div>
            </div>
          </div>

          {/* Détails de la réservation */}
          <div className={`bg-gray-50 rounded-lg p-3 sm:p-4 transition-all duration-700 delay-300 ${showAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h2 className="font-semibold text-sm sm:text-base text-gray-900 mb-2 sm:mb-3">Réservation</h2>
            <div className="text-xs sm:text-sm space-y-1 sm:space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Début:</span>
                <span className="font-medium text-right">
                  {new Date(bookingDetails.startDate).toLocaleDateString('fr-FR')} {bookingDetails.startTime}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Fin:</span>
                <span className="font-medium text-right">
                  {new Date(bookingDetails.endDate).toLocaleDateString('fr-FR')} {bookingDetails.endTime}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Durée:</span>
                <span className="font-medium">{bookingDetails.totalHours}h</span>
              </div>
              <div className="flex justify-between border-t pt-1 sm:pt-2">
                <span className="text-gray-600 font-semibold">Total:</span>
                <span className="font-bold text-green-600 text-base sm:text-lg">{bookingDetails.totalPrice.toFixed(2)}€</span>
              </div>
              {rentalId && (
                <div className="flex justify-between border-t pt-1 sm:pt-2">
                  <span className="text-gray-600">ID Trajet:</span>
                  <span className="font-medium text-indigo-600">#{rentalId}</span>
                </div>
              )}
            </div>
          </div>

          {/* QR Code et Instructions */}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 transition-all duration-700 delay-400 ${showAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            
            {/* QR Code */}
            <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-3 sm:p-4 text-center h-32 sm:h-40 flex items-center justify-center">
              <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white rounded-lg flex items-center justify-center">
                {qrCodeDataUrl ? (
                  <img 
                    src={qrCodeDataUrl} 
                    alt="QR Code" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-lg sm:text-xl">📱</div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-blue-900 text-xs sm:text-sm mb-2 sm:mb-3">Instructions</h3>
              <ul className="text-xs sm:text-sm text-blue-800 space-y-1 sm:space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-1">1.</span>
                  <span>Allez vers la mobilité</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-1">2.</span>
                  <span>Scannez le QR ou donnez le code</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-1">3.</span>
                  <span>Portez le casque de sécurité</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-1">4.</span>
                  <span>Go Dingo !</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Trip Token et Code d'accès */}
          <div className={`bg-indigo-50 rounded-lg p-3 sm:p-4 transition-all duration-700 delay-500 ${showAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h3 className="font-semibold text-indigo-900 text-xs sm:text-sm mb-2 sm:mb-3">Code d'accès du trajet</h3>
            <div className="bg-white border-2 border-indigo-300 rounded-lg p-2 sm:p-3 text-center">
              <span className="font-mono font-bold text-lg sm:text-xl text-indigo-800">{trip_token}</span>
            </div>
            <p className="text-xs sm:text-sm text-indigo-700 mt-1 sm:mt-2">Utilisez ce code si le QR ne fonctionne pas</p>
          </div>

        </div>

        {/* Boutons d'action */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mt-4 sm:mt-6 transition-all duration-700 delay-600 ${showAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <button
            onClick={handleViewRentals}
            className="py-2 px-3 sm:py-2.5 sm:px-4 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors text-xs sm:text-sm"
          >
            Mes trajets
          </button>
          
          <button
            onClick={handleShareBooking}
            className="py-2 px-3 sm:py-2.5 sm:px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-xs sm:text-sm"
          >
            Partager
          </button>
          
          <button
            onClick={handleBackToOverview}
            className="py-2 px-3 sm:py-2.5 sm:px-4 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-xs sm:text-sm"
          >
            Explorer
          </button>
        </div>

      </div>
    </div>
  );
};

export default BookingConfirmationPage; 